import { EnhancedWithAuthHttpService } from './http-auth.service'
import { axiosInstance } from './http-common.service'
import { HttpService } from './http.service'

import {
  IAccountsAllRawRespDto,
  ICancelRemovalDto,
  IDraftScanCreateDto,
  IDraftScansRespDto,
  IImpactByIdWithQuantityRespDto,
  IMessageResponseDto,
  IProductsAllRawRespDto,
  IQRCodeScanDto,
  IQRCodeScanRespDto,
  IScanAccountImpactsRespDto,
  IScanCreateDto,
  IScanCreateInitialValuesResp,
  IScanIdsRespDto,
  IScanMessageResponseWithBalance,
  IScanProductImpactsRespDto,
  IScanReferenceIdsRespDto,
  IScansRespDto,
  IScansStatusCount,
  IScanUpdateReviewedDto,
  IScanUpdateStatusDto,
  IStepsAllRawRespDto,
  IUnreviewedScansCountRespDto,
} from 'src/types'

class ScanService extends EnhancedWithAuthHttpService {
  constructor() {
    super(new HttpService(axiosInstance))
  }

  createScan(dto: IScanCreateDto) {
    return this.post<IScanMessageResponseWithBalance, IScanCreateDto>(`scans`, dto)
  }

  updateScanReviewed(dto: IScanUpdateReviewedDto) {
    return this.put<IMessageResponseDto, Omit<IScanUpdateReviewedDto, 'id'>>(
      `scans/${dto.id}/reviewed`,
      { reviewed: dto.reviewed, accountId: dto.accountId },
    )
  }

  updateScanStatus(dto: IScanUpdateStatusDto) {
    return this.put<IMessageResponseDto, Omit<IScanUpdateStatusDto, 'id'>>(
      `scans/${dto.id}/status`,
      { status: dto.status, accountId: dto.accountId },
    )
  }

  deleteScan(scanId: string) {
    return this.delete<IMessageResponseDto>(`scans/${scanId}`, {})
  }

  cancelScanRemove(scanId: string) {
    return this.get<IMessageResponseDto>('scans/cancel-deletion', { params: { scanId } })
  }

  pushScanToBc(scanId: string) {
    return this.put<IScanMessageResponseWithBalance, undefined>(`scans/${scanId}/pushed`, undefined)
  }

  getUnreviewedScansCount(accountId?: string) {
    return this.get<IUnreviewedScansCountRespDto>(`scans/unreviewed/count`, {
      params: { accountId },
    })
  }

  getCreateScanInitialValues(params?: {}) {
    return this.get<IScanCreateInitialValuesResp>(`scans/create-initial`, { params })
  }

  createDraftScan(dto: IDraftScanCreateDto) {
    return this.post<IMessageResponseDto, IDraftScanCreateDto>(`scans/drafts`, dto)
  }

  getDraftScans(params?: {}) {
    return this.get<IDraftScansRespDto>(`scans/drafts`, { params })
  }

  getDraftScansCount() {
    return this.get<number>(`scans/drafts/count`)
  }

  getScans(params?: {}) {
    return this.get<IScansRespDto>(`scans`, { params })
  }

  getScansStatusCount(params: {}, isScanner: boolean) {
    return this.get<IScansStatusCount>(`scans/status-count`, {
      params: { ...params, isScanner: isScanner },
    })
  }

  scanQrCode(dto: IQRCodeScanDto) {
    return this.post<IQRCodeScanRespDto, IQRCodeScanDto>(`scans/qr-code`, dto)
  }

  getReferenceIds(productId: string) {
    return this.get<IScanReferenceIdsRespDto>(`scans/reference-ids/${productId}`)
  }

  getBatchIds(productId: string) {
    return this.get<IScanIdsRespDto>(`scans/batch-ids/${productId}`)
  }

  getScanProducts(accountId: string, params?: {}) {
    return this.get<IProductsAllRawRespDto>(`scans/products/${accountId}`, { params })
  }

  getScanAccounts(params?: {}) {
    return this.get<IAccountsAllRawRespDto>(`scans/accounts`, { params })
  }

  getScanSteps(productId: string, params?: {}) {
    return this.get<IStepsAllRawRespDto>(`scans/steps/${productId}`, { params })
  }

  getScanAccountImpacts(accountId: string, params?: {}) {
    return this.get<IScanAccountImpactsRespDto>(`scans/account/impacts/${accountId}`, { params })
  }

  getScanAccountImpactWithQuantity(accountId: string, impactId: string) {
    return this.get<IImpactByIdWithQuantityRespDto>(`scans/account/impact/${accountId}/${impactId}`)
  }

  getScanProductImpacts(productId: string, params?: {}) {
    return this.get<IScanProductImpactsRespDto>(`scans/product/impacts/${productId}`, { params })
  }

  getScanProductImpactWithQuantity(productId: string, impactId: string) {
    return this.get<IImpactByIdWithQuantityRespDto>(`scans/product/impact/${productId}/${impactId}`)
  }

  removeScanDrafts(ids: string[]) {
    return this.delete<string>(`scans/drafts`, { data: { ids } })
  }

  cancelScanDraftsRemove(params: ICancelRemovalDto) {
    return this.get<IMessageResponseDto>('scans/drafts/cancel-deletion', { params })
  }
}

export const scanService = new ScanService()
